/**
 * TranslationChangesPanel.tsx — PLAT-10444 (epic PLAT-10209)
 *
 * The Translation Review workspace's "Changes" tab body. Lists every word /
 * segment whose APPLIED translation differs from the machine baseline — both
 * single-phrase edits and span-replace override segments — showing for each the
 * original (source) text, the previous (machine) translation, the current
 * (edited) translation, who made the change, and the source word's transcript
 * time. Each card stacks the values vertically, source-first ("Original Text"
 * → "Original Translation" → "Your Translation", full card width per value) so
 * long changes stay readable in the narrow right pane. Clicking a change seeks
 * the playhead to that word (moving both panes' cursors).
 *
 * Derives from the CURRENT active changed set (not the changelog history) by
 * reusing the shared changed-phrase helpers (`resolveChangedPhrase`,
 * `buildOverrideOriginalMap`, `resolveSourceText`), so re-edited or reverted
 * items fall out naturally with no history reconciliation. Each card is also an
 * edit surface: "Your Translation" is inline click-to-edit, and Revert restores
 * the machine translation — the same store ops the transcript panes' edit
 * popover uses, so the two surfaces stay consistent.
 */

import { trackTranslationReverted } from '@src/analytics/EventTracker';
import {
  useStore,
  useTranscription,
  useTranslationStore,
} from '@src/stores-v2/StoreContext';
import { joinFragments } from '@src/stores-v2/translationSpanEdit';
import { formatEditedAt } from '@src/utility/dateFormat';
import { convertTimeToMMSS } from '@src/utility/timeFormat';
import { seekToInitialIndex } from '@src/utility/transcriptEvents';
import { observer } from 'mobx-react-lite';
import React from 'react';
import ReactDOM from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import {
  buildOverrideOriginalMap,
  fragmentKeyOf,
  resolveChangedPhrase,
  resolveFragmentTmEntry,
  resolveProvenanceLabel,
  resolveSeekTarget,
} from './changedPhraseKit';
import {
  COVERAGE_TOOLTIP_STYLE,
  CoverageBadgeWithTooltip,
} from './CoverageBadge';
import {
  Card,
  Empty,
  Field,
  FieldLabel,
  FieldValue,
  List,
  Meta,
  NowValue,
  OriginalValue,
} from './reviewCardPrimitives';
import { resolveSourceText } from './translatedPhraseEditing';
import {
  DisclosureChevron,
  InlineTextAction,
} from './translationEditorPrimitives';

import type { TmPromoteSeed } from './changedPhraseKit';
import type { TranslationMemoryEntry } from '@src/repositories/TranslationMemoryEntryRepository';
import type { ITranscriptVideoCreator } from '@src/stores/ITranscriptVideoCreator';
import type { TranscriptElement } from '@src/types.ts/transcript';
import type {
  CoverageVerdict,
  TranslationPhrase,
} from '@src/types.ts/translation';

interface ChangeEntry {
  /** Stable react key — the override id, or the phrase's source indices. */
  key: string;
  /** Original-language source text behind the change. */
  source: string;
  /** Previous (machine) translation; absent when the baseline is unknown. */
  before: string | undefined;
  /** Current (edited) translation. */
  after: string;
  /** Reviewer who made the change; absent on edits made before attribution shipped. */
  changedBy: string | undefined;
  /** Edit timestamp (ISO); absent on legacy edits. Sorts most-recent-first. */
  at: string | undefined;
  /** Source `initial_index` to seek to (first seekable word); absent if none. */
  seekIndex: number | undefined;
  /** Transcript time of that word in seconds, for the mm:ss label. */
  seekTs: number | undefined;
  /** All source indices behind the change — drives the hover cross-pane highlight. */
  originalIndices: readonly number[];
  /**
   * Stable source-word ids behind the change (PLAT-11335) — the key the
   * coverage cache resolves by. Empty on pre-PLAT-10611 phrases, which are
   * never coverage-checked anyway.
   */
  originalElementIds: readonly string[];
  /** Honored translation-memory entry ids stamped by the backend (PLAT-10843). */
  tmEntryIds: readonly string[] | undefined;
  /**
   * Machine-steered provenance label — `AI re-translated · "<instruction>"` for
   * a directed override (PLAT-10257 A5); absent on manual changes so their meta
   * line stays exactly as before. Resolved from stored data only
   * (`resolveProvenanceLabel`: segment `origin` + joined changelog entry).
   */
  provenance: string | undefined;
}

interface TranslationChangesPanelProps {
  language: string;
  /**
   * The scoped video creator for this review surface. Clicking a change seeks
   * its playhead to the source word, which moves the cursor in both aligned
   * panes (they track the shared time). Same helper the panes' word-click uses.
   */
  videoCreator: ITranscriptVideoCreator;
  /**
   * Hovering a card reports the change's source indices (null on leave) so the
   * host can paint the correspondence highlight in both aligned columns —
   * the Google-Translate-style alignment reveal. Optional / opt-in (D9).
   */
  onEntryHover?: (originalIndices: readonly number[] | null) => void;
  /**
   * Clicking a card also asks the host to scroll both aligned columns to the
   * change (the playback autoscroll is playing-only, so a paused seek alone
   * leaves the change offscreen). Optional / opt-in (D9).
   */
  onEntryReveal?: (initialIndex: number) => void;
  /**
   * Entry key of the change the reviewer is hovering IN THE TRANSCRIPT (a
   * changed green phrase) — the reverse of `onEntryHover`. The matching card
   * lifts to the elevated background and scrolls into view. Keys match
   * `ChangeEntry.key` (override id or `f:<originalIndices>`).
   */
  highlightedEntryKey?: string | null;
  /**
   * PLAT-10852 — the workspace's glossary entries + the ids promoted in this
   * session, so a change card can tell the reviewer that the fragment they just
   * edited IS a translation-memory entry (and link to it) rather than leaving
   * that fact only on the overlay. Best-effort: absent/unresolved ⇒ the card
   * renders exactly as before.
   */
  tmEntriesById?: ReadonlyMap<string, TranslationMemoryEntry>;
  promotedTmEntryIds?: ReadonlyMap<string, string>;
  /** Open the entry behind a change card. The host owns the modal. */
  onViewTmEntry?: (entry: TranslationMemoryEntry) => void;
  /**
   * PLAT-11335 — resolves the fresh coverage verdict covering a change's stable
   * source ids (the workspace's cache, by overlap). Present ⇒ each checked
   * change's card shows the coverage badge on its "Your Translation" row, with
   * the verdict's explanation as a hover tooltip — Dmitry's 8/18 ask to see the
   * quality check in the change log, not only on the segment. Absent (or no
   * fresh verdict for a change) ⇒ the card renders exactly as before.
   */
  resolveCoverage?: (
    originalElementIds: readonly string[],
  ) => CoverageVerdict | undefined;
  /**
   * PLAT-10852 — promote this change to a glossary entry, seeded with the
   * card's source text and the translation currently in the editor. Offered
   * only while the card is being edited, mirroring the edit overlay: promoting
   * is a decision about a translation you are authoring, not a browsing action.
   */
  onPromoteToTm?: (seed: TmPromoteSeed) => void;
  /**
   * PLAT-11678 — a change was reverted; these are the source-element ids it
   * covered. Reverting applies at once (no debounced save), and the dub for
   * the segment may now match an earlier recording or need re-voicing — the
   * host decides. Fires after the store call, never on a failed one.
   */
  onReverted?: (originalElementIds: string[]) => void;
  /**
   * PLAT-11678 — a change card's "Your Translation" was retyped and committed;
   * these are the source-element ids it covered. Same spoken-text change as the
   * transcript-pane editors, so the host feeds it to the auto re-dub. Fires
   * after the store call, only on a non-empty, actually-changed draft.
   */
  onCommitted?: (originalElementIds: string[]) => void;
}

export const TranslationChangesPanel: React.FC<TranslationChangesPanelProps> =
  observer(function TranslationChangesPanel({
    language,
    videoCreator,
    onReverted,
    onCommitted,
    onEntryHover,
    onEntryReveal,
    highlightedEntryKey,
    tmEntriesById,
    promotedTmEntryIds,
    onViewTmEntry,
    onPromoteToTm,
    resolveCoverage,
  }) {
    const translationStore = useTranslationStore();
    const transcriptionStore = useTranscription();
    const rootStore = useStore();

    // Scroll the transcript-hover-highlighted card into view. Scoped to the
    // list's own ref (CLAUDE.md #12); `scrollIntoView` is absent in jsdom,
    // hence the optional call.
    const listRef = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
      if (!highlightedEntryKey) return;
      listRef.current
        ?.querySelector(`[data-entry-key="${CSS.escape(highlightedEntryKey)}"]`)
        ?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    }, [highlightedEntryKey]);

    const translation = translationStore.getTranslation(language);
    const phrases = translation?.phrases ?? [];
    const overrideMap = buildOverrideOriginalMap(phrases);
    const baseElements = transcriptionStore.getFinalTranscriptionElements();
    const byIndex = new Map<number, TranscriptElement>();
    for (const el of baseElements) byIndex.set(el.initial_index, el);
    const segments = translationStore.getSegments(language);

    const entries: ChangeEntry[] = [];
    for (const seg of segments) {
      // A span-replace override (`o_…`) is authored as a whole; show one entry
      // for the segment, not one per derived fragment.
      const isOverride = seg.parent?.id.startsWith('o_') ?? false;
      if (isOverride && seg.parent) {
        const sourcePhrase: TranslationPhrase = {
          ...seg.fragments[0],
          originalValueSnapshot: seg.parent.originalValueSnapshot ?? '',
          originalIndices: seg.parent.originalIndices,
        };
        const seek = resolveSeekTarget(seg.parent.originalIndices, byIndex);
        entries.push({
          key: seg.parent.id,
          source: resolveSourceText(sourcePhrase, baseElements),
          before: overrideMap.get(seg.parent.id),
          after: joinFragments(seg.fragments),
          changedBy: seg.parent.editedBy?.name,
          at: seg.parent.editedBy?.at,
          seekIndex: seek?.index,
          seekTs: seek?.ts,
          originalIndices: seg.parent.originalIndices,
          originalElementIds: seg.fragments.flatMap(
            f => f.originalElementIds ?? [],
          ),
          tmEntryIds: seg.fragments.flatMap(f => f.tmEntryIds ?? []),
          provenance: resolveProvenanceLabel(
            seg.parent,
            translation?.changelog,
          ),
        });
        continue;
      }
      // Single-phrase edits (including edits inside a machine `p_` segment).
      for (const fragment of seg.fragments) {
        const { isEdited, previousValue } = resolveChangedPhrase(
          fragment,
          phrases,
          overrideMap,
        );
        if (!isEdited) continue;
        const seek = resolveSeekTarget(fragment.originalIndices, byIndex);
        entries.push({
          key: `f:${fragment.originalIndices.join(',')}`,
          source: resolveSourceText(fragment, baseElements),
          before: previousValue,
          after: fragment.translatedValue,
          changedBy: fragment.editedBy?.name,
          at: fragment.editedBy?.at,
          seekIndex: seek?.index,
          seekTs: seek?.ts,
          originalIndices: fragment.originalIndices,
          originalElementIds: fragment.originalElementIds ?? [],
          tmEntryIds: fragment.tmEntryIds,
          // Single-phrase edits are hand-typed by definition — a directed
          // re-translate always authors an `o_…` override segment.
          provenance: undefined,
        });
      }
    }
    // Most-recent change first. Entries without a timestamp (legacy edits) sort
    // last; ISO strings compare lexicographically in time order.
    entries.sort((a, b) => (b.at ?? '').localeCompare(a.at ?? ''));

    if (entries.length === 0) {
      return <Empty>No changes yet.</Empty>;
    }

    // Inline "Your Translation" edit → commit to the store, routed by entry
    // type: an override segment (key = its `o_…` id) updates that segment; a
    // single-phrase edit re-edits the phrase by its source indices. No-op on an
    // empty or unchanged draft. These are the SAME store ops the transcript-pane
    // editors use, so the two edit surfaces stay consistent.
    const commitEdit = (entry: ChangeEntry, value: string) => {
      const trimmed = value.trim();
      if (!trimmed || trimmed === entry.after) return;
      if (entry.key.startsWith('o_')) {
        void translationStore.updateOverrideSegment(
          language,
          entry.key,
          trimmed,
        );
      } else {
        translationStore.editTranslatedPhrase(
          language,
          [...entry.originalIndices],
          trimmed,
        );
      }
      // PLAT-11678 — the retype changed the spoken text; feed the auto re-dub
      // the same way the transcript-pane editors do (the transcript surfaces go
      // through `onPhraseEditSaved`). Without this a change-card edit saves but
      // leaves the dub voiced from the old words.
      onCommitted?.([...entry.originalElementIds]);
    };

    // Revert a change back to the machine translation — the SAME shape-aware
    // routing the edit popover's Revert uses (`usePhraseEditing.handleRevert`):
    // an override segment has no per-phrase machine original (its baseline lives
    // on the retired `supersededBy` fragments), so it is dropped whole; every
    // other change reverts by its stable source-word ids. No confirm, matching
    // the popover — the card disappearing from the list IS the confirmation,
    // and the reviewer can retype the edit.
    const revertEntry = (entry: ChangeEntry) => {
      if (entry.key.startsWith('o_')) {
        void translationStore.revertOverrideSegment(language, entry.key);
      } else {
        translationStore.revertTranslation(language, [
          ...entry.originalElementIds,
        ]);
      }
      trackTranslationReverted({
        story_id: rootStore.storyId ?? undefined,
        language,
      });
      onReverted?.([...entry.originalElementIds]);
    };

    return (
      <List ref={listRef}>
        {entries.map(entry => (
          <ChangeCard
            key={entry.key}
            entry={entry}
            coverage={resolveCoverage?.(entry.originalElementIds)}
            seekable={entry.seekIndex !== undefined}
            highlighted={entry.key === highlightedEntryKey}
            onSeek={() => {
              if (entry.seekIndex === undefined) return;
              seekToInitialIndex(videoCreator, baseElements, entry.seekIndex);
              // The playback autoscroll is playing-only — ask the host to
              // scroll both columns to the change so a paused click still
              // lands the reviewer on it (CAT-tool "focus the segment").
              onEntryReveal?.(entry.seekIndex);
            }}
            onHover={onEntryHover}
            onCommitEdit={commitEdit}
            onRevert={revertEntry}
            tmEntry={resolveFragmentTmEntry(
              fragmentKeyOf(entry.originalIndices),
              entry.tmEntryIds,
              tmEntriesById,
              promotedTmEntryIds,
            )}
            onViewTmEntry={onViewTmEntry}
            onPromoteToTm={onPromoteToTm}
          />
        ))}
        {/* PLAT-11335 — ONE portaled tooltip serves every card's coverage badge
            (the UndoRedoButtons idiom); each badge binds via data attributes. */}
        {resolveCoverage &&
          ReactDOM.createPortal(
            <Tooltip
              id="changes-coverage-tooltip"
              style={COVERAGE_TOOLTIP_STYLE}
              positionStrategy="fixed"
            />,
            document.body,
          )}
      </List>
    );
  });

/**
 * One change card. Local UI state only: the "Original Translation" row is a
 * disclosure collapsed by default (PLAT-10702), and "Your Translation" is an
 * inline click-to-edit field — click the value to open a textarea, Enter or
 * blur commits, Escape cancels. `entry` is a fresh snapshot each parent render;
 * the draft re-syncs to it whenever we are NOT mid-edit.
 */
const ChangeCard: React.FC<{
  entry: ChangeEntry;
  /** Fresh coverage verdict for this change (PLAT-11335); absent ⇒ no badge. */
  coverage?: CoverageVerdict;
  seekable: boolean;
  highlighted: boolean;
  onSeek: () => void;
  onHover?: (originalIndices: readonly number[] | null) => void;
  onCommitEdit: (entry: ChangeEntry, value: string) => void;
  /** Restore the machine translation, dropping this change. */
  onRevert: (entry: ChangeEntry) => void;
  /** The translation-memory entry this change's fragment is (PLAT-10852). */
  tmEntry?: TranslationMemoryEntry | null;
  onViewTmEntry?: (entry: TranslationMemoryEntry) => void;
  onPromoteToTm?: (seed: TmPromoteSeed) => void;
}> = ({
  entry,
  coverage,
  seekable,
  highlighted,
  onSeek,
  onHover,
  onCommitEdit,
  onRevert,
  tmEntry,
  onViewTmEntry,
  onPromoteToTm,
}) => {
  const [prevOpen, setPrevOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(entry.after);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    if (!editing) setDraft(entry.after);
  }, [entry.after, editing]);

  React.useEffect(() => {
    if (!editing) return;
    const ta = textareaRef.current;
    if (!ta) return;
    ta.focus();
    ta.setSelectionRange(ta.value.length, ta.value.length);
  }, [editing]);

  const commit = () => {
    setEditing(false);
    onCommitEdit(entry, draft);
  };
  const cancel = () => {
    setDraft(entry.after);
    setEditing(false);
  };

  // Clicking the card seeks to the change; suppressed while editing so a click
  // in the field (or the blur-to-commit) never also jumps the playhead.
  const cardClickable = seekable && !editing;

  return (
    <Card
      data-entry-key={entry.key}
      $highlighted={highlighted}
      $clickable={cardClickable}
      role={seekable ? 'button' : undefined}
      tabIndex={cardClickable ? 0 : undefined}
      title={cardClickable ? 'Jump to this spot in the transcript' : undefined}
      onClick={cardClickable ? onSeek : undefined}
      onMouseEnter={onHover ? () => onHover(entry.originalIndices) : undefined}
      onMouseLeave={onHover ? () => onHover(null) : undefined}
      onKeyDown={
        cardClickable
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSeek();
              }
            }
          : undefined
      }
    >
      <Field>
        <FieldLabel>Original Text</FieldLabel>
        <OriginalValue>{entry.source}</OriginalValue>
      </Field>

      {
        // No baseline → no row, mirroring the popovers' hidden machine-value
        // row (never an empty placeholder). Collapsed by default (PLAT-10702).
      }
      {entry.before !== undefined && (
        <Field>
          {/* Revert sits on the machine value's own row rather than in a card
              footer, because it restores THIS value — the same placement (and
              the same `InlineTextAction` primitive) as the edit popover's
              Revert, PLAT-10644. Visible whether or not the disclosure is
              open, so reverting never costs an extra click. */}
          <CardLabelRow>
            <CollapsibleLabel
              type="button"
              aria-expanded={prevOpen}
              onClick={e => {
                e.stopPropagation();
                setPrevOpen(o => !o);
              }}
            >
              Original Translation
              <DisclosureChevron expanded={prevOpen} size={9} />
            </CollapsibleLabel>
            <InlineTextAction
              type="button"
              data-testid="change-revert"
              title="Restore the original translation"
              // The textarea commits on blur, so a plain click while editing
              // would unmount this button before the click resolved (the
              // `TmActionLink` idiom). Keep focus; the revert wins either way.
              onMouseDown={e => e.preventDefault()}
              onClick={e => {
                e.stopPropagation();
                onRevert(entry);
              }}
            >
              Revert
            </InlineTextAction>
          </CardLabelRow>
          {prevOpen && <WasValue>{entry.before}</WasValue>}
        </Field>
      )}

      <Field>
        <CardLabelRow>
          <FieldLabel>Your Translation</FieldLabel>
          {coverage && (
            <CoverageBadgeWithTooltip
              verdict={coverage}
              tooltipId="changes-coverage-tooltip"
            />
          )}
        </CardLabelRow>
        {editing ? (
          <EditTextarea
            ref={textareaRef}
            value={draft}
            rows={3}
            aria-label="Edit your translation"
            onClick={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
            onChange={e => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={e => {
              e.stopPropagation();
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                commit();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                cancel();
              }
            }}
          />
        ) : (
          <EditableNowValue
            title="Click to edit"
            onClick={e => {
              e.stopPropagation();
              setDraft(entry.after);
              setEditing(true);
            }}
          >
            {/* A REMOVED translation has no text to show (PLAT-11340 f/u).
                Rendering `entry.after` raw left an empty, apparently broken
                click target — and the card is the reviewer's record of what
                they did, so the one change it must not render as nothing is a
                deletion. Named instead, in the muted prose treatment, so the
                card reads as deliberate; clicking it still opens the editor,
                which is how text is typed back. */}
            {entry.after.trim() === '' ? (
              <RemovedNote>Removed</RemovedNote>
            ) : (
              entry.after
            )}
          </EditableNowValue>
        )}
      </Field>

      {/* PLAT-10852 — promote the translation being authored, mirroring the
          edit overlay's action. Shown only while EDITING (and only when the
          fragment isn't already an entry — that case gets the link below):
          promoting is a decision about a value you are writing, so outside edit
          mode the card stays a read-only summary.

          `onMouseDown` preventDefault is load-bearing: the textarea commits on
          blur, so a plain click would tear down edit mode and unmount this
          button before the click resolved. Keeping focus lets the click land,
          and we seed from `draft` — the text on screen, not the committed
          `entry.after`, which may still be a keystroke behind. */}
      {editing && !tmEntry && onPromoteToTm && (
        <TmActionLink
          type="button"
          onMouseDown={e => e.preventDefault()}
          onClick={e => {
            e.stopPropagation();
            onPromoteToTm({
              fragmentKey: fragmentKeyOf(entry.originalIndices),
              sourceTerm: entry.source,
              translation: draft,
            });
          }}
        >
          Save to Translation Memory
        </TmActionLink>
      )}

      {/* PLAT-10852 — the fragment IS a translation-memory entry: say so here,
          not only on the overlay, and link straight to it. stopPropagation so
          the link never triggers the card's seek. */}
      {tmEntry && onViewTmEntry && (
        <TmActionLink
          type="button"
          onClick={e => {
            e.stopPropagation();
            onViewTmEntry(tmEntry);
          }}
        >
          Translation Memory Entry
        </TmActionLink>
      )}

      <Meta>
        {[
          // Machine-steered changes lead with their provenance (PLAT-10257 A5);
          // manual changes have none, keeping their meta line unchanged.
          entry.provenance,
          entry.changedBy ? `by ${entry.changedBy}` : undefined,
          entry.at ? formatEditedAt(entry.at) : undefined,
          entry.seekTs !== undefined
            ? convertTimeToMMSS(entry.seekTs)
            : undefined,
        ]
          .filter(Boolean)
          .join(' • ')}
      </Meta>
    </Card>
  );
};

// PLAT-10852 — the card's glossary affordance, shared by its two mutually
// exclusive forms: "Translation Memory Entry" when the fragment already is one,
// and "Save to Translation Memory" while editing when it isn't. Green
// `--primary` per the design system's action colour, quiet enough not to
// compete with the card's three values.
const TmActionLink = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  color: var(--primary);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: var(--foreground);
  }
`;

// The superseded machine value, revealed by the disclosure. Carries the same
// 2px left rule as "Original Text" above it so the two reference values line up
// as a pair, and is bold so the previous wording stands out against the label
// stack when the reviewer opens it to compare.
const WasValue = styled(FieldValue)`
  color: var(--text-muted);
  font-weight: var(--font-weight-bold);
  border-left: 2px solid var(--border-elevated);
  padding-left: 8px;
`;

// The current translation as an inline click-to-edit affordance: a quiet inset
// box appears on hover to signal it is editable, without shifting layout.
// Carries the same 2px left rule as the two values above it while it reads as
// TEXT; the rule is deliberately absent from `EditTextarea` below, because once
// the row becomes a real input the field's own box is the boundary and a second
// rule beside it reads as a stray artifact.
// The "Removed" stand-in for a deleted translation's empty value. Muted +
// italic marks it as a state label rather than translated text a reader might
// mistake for content (design system: prose is sentence case, and this is
// prose, not a button).
const RemovedNote = styled.span`
  color: var(--text-muted);
  font-style: italic;
`;

const EditableNowValue = styled(NowValue)`
  cursor: text;
  border-left: 2px solid var(--border-elevated);
  border-radius: 0 4px 4px 0;
  padding: 2px 4px 2px 8px;
  margin: -2px -4px -2px 0;
  transition: background 120ms ease;

  &:hover {
    background: var(--background);
  }
`;

// Inline editor for "Your Translation". Neutral gray focus ring per the design
// system's text-field treatment (green/gold are reserved for actions/controls).
const EditTextarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
  min-height: 52px;
  font-family: inherit;
  font-size: var(--text-xs);
  line-height: 1.4;
  color: var(--foreground);
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: var(--radius-input, 6px);
  padding: 6px 8px;

  &:focus {
    outline: none;
    border-color: var(--focus-border);
    box-shadow: 0 0 0 1px var(--focus-ring);
  }
`;

// "Original Translation" label as a disclosure toggle (collapsed by default).
// Matches the FieldLabel scale/color so the row still reads as a quiet label.
const CollapsibleLabel = styled.button`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 4px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 11px;
  font-weight: var(--font-weight-normal);
  color: var(--text-muted);

  &:hover {
    color: var(--text-secondary);
  }
`;

/** "Your Translation" label with the coverage badge pinned right (PLAT-11335). */
const CardLabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;
